import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { VirtualKeyboard } from '@/components/VirtualKeyboard/VirtualKeyboard';
import {
  useAddMdiHistory,
  useClearMdiConsole,
  useClearMdiHistory,
  useFeedOverride,
  useFeedSpindle,
  useIsLocked,
  useMdiConsole,
  useMdiHistory,
  useMdiProgress,
  useSendCommand,
  useSpindleOn,
  useSpindleOverride,
} from '@/store/store';
import { isCriticalError } from '@/types/machine.types';
import styles from '@/components/jog/jog.module.css';

// Format an F/S read-back value: round off floating-point noise (so a whole
// value reads "1200" rather than "1200.0000001").
function formatFs(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/**
 * MDI (Manual Data Input) page: type a G/M-code line and Send it straight to
 * LinuxCNC (RUN_MDI with that single line); each sent line is also logged in the
 * history list below. The backend executes on INTERP_IDLE; the counter/progress
 * bar reflect that real machine execution. Send is blocked by the safety
 * interlock (E-Stop / un-homed) and while a line is still running.
 * The keyboard's "Set" key is intentionally disabled for now.
 */
export function MDIPanel(): JSX.Element {
  const locked = useIsLocked();
  const sendCommand = useSendCommand();
  const mdi = useMdiProgress();
  // Effective F/S at the motors: the backend reports the programmed F/S
  // (LinuxCNC settings[1]/[2]); scale it by the override the operator sets from
  // the UI so the readout tracks the override instead of the raw G-code value.
  const { feedRate, spindleSpeed } = useFeedSpindle();
  const feedOverride = useFeedOverride();
  const spindleOverride = useSpindleOverride();
  const spindleOn = useSpindleOn();
  const effectiveFeedRate = (feedRate * feedOverride) / 100;
  // spindleSpeed is the commanded value and persists after M5. Show 0 when the
  // spindle is off so the readout matches the real state, not the last command.
  const effectiveSpindleSpeed = spindleOn ? (spindleSpeed * spindleOverride) / 100 : 0;
  // Sent MDI lines live in the store so they persist across tab switches
  // (unmounting this panel no longer wipes the log) until the app closes.
  const history = useMdiHistory();
  const addHistory = useAddMdiHistory();
  const clearHistory = useClearMdiHistory();
  const [input, setInput] = useState('');
  // Which logged line is recalled into the input (null = composing a fresh line),
  // set via the ↑/↓ history keys. Used to highlight that row.
  const [histIndex, setHistIndex] = useState<number | null>(null);
  const activeRowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Caret position to restore after a programmatic (on-screen keyboard) edit.
  const pendingCaret = useRef<number | null>(null);

  // Machine response console: LinuxCNC error_channel messages (errors, and the
  // (msg,…)/(debug,…) output MDI lines emit). The store keeps a durable transcript
  // so it survives tab switches and Clear actually empties it (a local mirror would
  // be rebuilt from the store's `errors` on remount, resurrecting cleared lines).
  const consoleLog = useMdiConsole();
  const clearConsole = useClearMdiConsole();
  const consoleEndRef = useRef<HTMLDivElement>(null);

  // Keep the active row in view — the newest staged line, or the line the
  // machine is currently executing while a queue runs.
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [history.length, histIndex, mdi.running, mdi.line]);

  // Keep the newest response line in view.
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ block: 'nearest' });
  }, [consoleLog.length]);

  // Restore the caret after a controlled-value update from the on-screen keyboard.
  useLayoutEffect(() => {
    if (pendingCaret.current === null || inputRef.current === null) return;
    const pos = pendingCaret.current;
    inputRef.current.setSelectionRange(pos, pos);
    pendingCaret.current = null;
  });

  // Insert text at the caret (on-screen keyboard character / symbol keys).
  const insertText = useCallback((text: string) => {
    const el = inputRef.current;
    const value = el?.value ?? '';
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    pendingCaret.current = start + text.length;
    setInput(value.slice(0, start) + text + value.slice(end));
  }, []);

  // Delete the character (or selection) before the caret.
  const backspaceAtCaret = useCallback(() => {
    const el = inputRef.current;
    const value = el?.value ?? '';
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    if (start === 0 && start === end) return;
    const cutStart = start === end ? start - 1 : start;
    pendingCaret.current = cutStart;
    setInput(value.slice(0, cutStart) + value.slice(end));
  }, []);

  // Move the text caret one character left/right.
  const moveCaret = useCallback((dir: 'left' | 'right') => {
    const el = inputRef.current;
    if (el === null) return;
    el.focus();
    const pos =
      dir === 'left'
        ? Math.max(0, (el.selectionStart ?? 0) - 1)
        : Math.min(el.value.length, (el.selectionEnd ?? el.value.length) + 1);
    el.setSelectionRange(pos, pos);
  }, []);

  // Recall sent commands: up = older, down = newer (past the newest = fresh line).
  const navHistory = useCallback(
    (dir: 'up' | 'down') => {
      if (history.length === 0) return;
      setHistIndex((idx) => {
        if (dir === 'up') {
          const next = idx === null ? history.length - 1 : Math.max(0, idx - 1);
          const line = history[next] ?? '';
          pendingCaret.current = line.length;
          setInput(line);
          return next;
        }
        if (idx === null) return null;
        const next = idx + 1;
        if (next >= history.length) {
          pendingCaret.current = 0;
          setInput('');
          return null;
        }
        const line = history[next] ?? '';
        pendingCaret.current = line.length;
        setInput(line);
        return next;
      });
    },
    [history],
  );

  // Send button / physical Enter: run the current line on the machine now
  // (RUN_MDI with that single line) and log it in the history. Blocked by the
  // safety interlock and while a line is still executing.
  const send = useCallback(() => {
    const command = input.trim();
    if (command === '' || locked || mdi.running) return;
    sendCommand({ type: 'RUN_MDI', lines: [command] });
    addHistory(command);
    setHistIndex(null);
    setInput('');
  }, [input, locked, mdi.running, sendCommand, addHistory]);

  // Clear button: drop the history log. Explicit, manual action (mirrors Axis'
  // "Clear MDI history"). Disabled while a line runs so it can't wipe the log
  // out from under a live run.
  const clear = useCallback(() => {
    clearHistory();
    setHistIndex(null);
  }, [clearHistory]);

  const staged = history.length;
  // While a queue runs, the counter/highlight track real machine progress
  // (mdiLine is 0-based). Idle, they reflect the staged list.
  const total = mdi.running ? mdi.total : staged;
  const current = mdi.running ? mdi.line + 1 : 0;
  const activeIndex = mdi.running ? mdi.line : (histIndex ?? staged - 1);

  return (
    <div className={styles.mdiPanel}>
      {/* Active programmed F/S read-back from the machine */}
      <section className={styles.mdiReadout}>
        <div className={styles.mdiReadoutItem}>
          <span className={styles.mdiReadoutLabel}>Feed rate</span>
          <span className={styles.mdiReadoutValue}>{formatFs(effectiveFeedRate)}</span>
          <span className={styles.mdiReadoutUnit}>/min</span>
        </div>
        <div className={styles.mdiReadoutItem}>
          <span className={styles.mdiReadoutLabel}>Spindle speed</span>
          <span className={styles.mdiReadoutValue}>{formatFs(effectiveSpindleSpeed)}</span>
          <span className={styles.mdiReadoutUnit}>rpm</span>
        </div>
      </section>

      {/* MDI command input */}
      <section className={styles.mdiCard}>
        <h3 className={styles.mdiCardTitle}>MDI command</h3>
        <div className={styles.mdiInputRow}>
          <input
            ref={inputRef}
            className={styles.mdiInput}
            type="text"
            value={input}
            placeholder="Enter G/M-code…"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => {
              setInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') send();
            }}
          />
          <button
            className={styles.mdiSendBtn}
            type="button"
            disabled={input.trim() === '' || locked || mdi.running}
            onClick={send}
          >
            Send
          </button>
          <button
            className={styles.mdiClearBtn}
            type="button"
            disabled={staged === 0 || mdi.running}
            onClick={clear}
          >
            Clear
          </button>
        </div>
      </section>

      {/* Command history */}
      <section className={styles.mdiHistory}>
        <div className={styles.mdiHistoryList}>
          {history.map((line, i) => {
            const active = i === activeIndex;
            return (
              <div
                key={i}
                ref={active ? activeRowRef : undefined}
                className={`${styles.mdiHistoryRow} ${active ? styles.mdiHistoryRowActive : ''}`}
              >
                <span className={styles.mdiHistoryGutter}>{String(i + 1).padStart(3, '0')}</span>
                <span className={styles.mdiHistoryCode}>{line}</span>
              </div>
            );
          })}
        </div>
        <span className={styles.mdiHistoryCount}>
          Line {String(current).padStart(3, '0')} / {total}
        </span>
      </section>

      {/* Inline keyboard — the Set key is intentionally disabled for now. */}
      <VirtualKeyboard
        onInput={insertText}
        onBackspace={backspaceAtCaret}
        submitDisabled
        onCaretMove={moveCaret}
        onHistoryNav={navHistory}
      />

      {/* Machine response console — LinuxCNC messages/errors, newest at the bottom. */}
      <section className={styles.mdiConsole}>
        <div className={styles.mdiConsoleHeader}>
          <h3 className={styles.mdiConsoleTitle}>Machine response</h3>
          <button
            className={styles.mdiConsoleClear}
            type="button"
            disabled={consoleLog.length === 0}
            onClick={clearConsole}
          >
            Clear
          </button>
        </div>
        <div className={styles.mdiConsoleList}>
          {consoleLog.length === 0 ? (
            <div className={styles.mdiConsoleEmpty}>No messages yet</div>
          ) : (
            consoleLog.map((entry) => (
              <div
                key={entry.id}
                className={`${styles.mdiConsoleRow} ${
                  isCriticalError(entry.kind) ? styles.mdiConsoleRowError : ''
                }`}
              >
                <span className={styles.mdiConsoleTime}>
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
                <span className={styles.mdiConsoleMsg}>{entry.message}</span>
              </div>
            ))
          )}
          <div ref={consoleEndRef} />
        </div>
      </section>
    </div>
  );
}
